import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {MvsCrudService} from '../../../../core/shared/service/crud/mvs-crud.service';
import {LogicFunctionDefinitionDto} from '../../model/dto/api/logic-function-definition.dto';
import {LogicFunctionDryRunRequestDto} from '../../model/dto/api/logic-function-dry-run-request.dto';
import {LogicFunctionDryRunResponseDto} from '../../model/dto/api/logic-function-dry-run-response.dto';

/**
 * Thin HTTP client for the backend {@code LogicFunctionRegistryController}.
 *
 * Exposes:
 *  - Definition discovery (all / by-namespace / single).
 *  - Namespace listing.
 *  - Dry-run dispatch for the structured Module-Function Editor.
 *
 * Endpoints mirror the controller mount at {@code /mvsa/lg/functions}.
 *
 * @ticket TM-1000001726
 */
@Injectable({providedIn: 'root'})
export class LogicFunctionRegistryService {

    /** Base path mirrors {@code LogicFunctionRegistryController} on the backend. */
    private readonly baseUrl = MvsCrudService.baseUrl + '/lg/functions';

    constructor(private http: HttpClient) {
    }

    /**
     * Lists ALL registered module-function definitions across every namespace.
     */
    listDefinitions(): Observable<LogicFunctionDefinitionDto[]> {
        return this.http.get<LogicFunctionDefinitionDto[]>(`${this.baseUrl}/definitions`);
    }

    /**
     * Lists definitions for one namespace, e.g. {@code "ns"} or {@code "tm"}.
     */
    listByNamespace(namespace: string): Observable<LogicFunctionDefinitionDto[]> {
        return this.http.get<LogicFunctionDefinitionDto[]>(
            `${this.baseUrl}/definitions/${encodeURIComponent(namespace)}`
        );
    }

    /**
     * Retrieves a single definition by namespace + function name.
     */
    getDefinition(namespace: string, functionName: string): Observable<LogicFunctionDefinitionDto> {
        return this.http.get<LogicFunctionDefinitionDto>(
            `${this.baseUrl}/definitions/${encodeURIComponent(namespace)}/${encodeURIComponent(functionName)}`
        );
    }

    /**
     * Lists all registered namespaces (provider keys).
     */
    listNamespaces(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/namespaces`);
    }

    /**
     * Performs a dry-run dispatch — providers MUST refrain from persisting
     * side-effects. Returns simulated {@code result} or an {@code error}
     * message.
     */
    dryRun(request: LogicFunctionDryRunRequestDto): Observable<LogicFunctionDryRunResponseDto> {
        return this.http.post<LogicFunctionDryRunResponseDto>(`${this.baseUrl}/dry-run`, request);
    }
}
